export interface PDFPageHighlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
  commentId?: string;
  highlightId?: string;
  active?: boolean;
  color?: string;
  kind?: 'comment' | 'highlight' | 'search' | 'selection';
}

export const PDF_PAGE_HIGHLIGHT_COLORS = {
  red: 'rgba(255, 84, 84, 0.52)',
  orange: 'rgba(255, 159, 64, 0.54)',
  yellow: 'rgba(255, 217, 51, 0.56)',
  green: 'rgba(104, 204, 88, 0.5)',
  teal: 'rgba(63, 206, 190, 0.5)',
  blue: 'rgba(88, 145, 214, 0.5)',
  purple: 'rgba(163, 99, 255, 0.5)',
  grey: 'rgba(150, 158, 168, 0.44)',
} as const;

export const PDF_PAGE_DEFAULT_HIGHLIGHT_COLOR =
  PDF_PAGE_HIGHLIGHT_COLORS.yellow;
export const PDF_PAGE_SELECTION_COLOR = 'rgba(88, 145, 214, 0.3)';
export const PDF_PAGE_COMMENT_COLOR = 'rgba(88, 145, 214, 0.34)';
export const PDF_PAGE_ACTIVE_OUTLINE_COLOR = 'rgba(38, 128, 214, 0.9)';
export const PDF_PAGE_COMMENT_UNDERLINE_COLOR = 'rgba(38, 128, 214, 0.82)';

const PDF_ALLOWED_RECT_COLORS = new Set<string>([
  ...Object.values(PDF_PAGE_HIGHLIGHT_COLORS),
  PDF_PAGE_SELECTION_COLOR,
  PDF_PAGE_COMMENT_COLOR,
]);

export function resolvePdfHighlightColor(
  value: string | undefined,
  fallback: string = PDF_PAGE_DEFAULT_HIGHLIGHT_COLOR
) {
  const trimmed = value?.trim();
  if (!trimmed) return fallback;
  return PDF_ALLOWED_RECT_COLORS.has(trimmed) ? trimmed : fallback;
}

export function toHighlightRectStyle(rect: PDFPageHighlightRect) {
  const fallback =
    rect.kind === 'comment'
      ? PDF_PAGE_COMMENT_COLOR
      : rect.kind === 'selection'
        ? PDF_PAGE_SELECTION_COLOR
        : PDF_PAGE_DEFAULT_HIGHLIGHT_COLOR;
  const color = resolvePdfHighlightColor(rect.color, fallback);
  return {
    left: `${rect.x * 100}%`,
    top: `${rect.y * 100}%`,
    width: `${rect.width * 100}%`,
    height: `${rect.height * 100}%`,
    ...(rect.kind
      ? {
          '--pdf-highlight-opacity':
            rect.kind === 'selection'
              ? '0.86'
              : rect.kind === 'search'
                ? '0.98'
                : '1',
        }
      : {}),
    ...(color
      ? {
          backgroundColor: color,
          boxShadow: `0 0 0 1px ${color}`,
        }
      : {}),
    ...(rect.active
      ? {
          outline: `2px solid ${PDF_PAGE_ACTIVE_OUTLINE_COLOR}`,
          outlineOffset: '1px',
        }
      : {}),
  };
}
